import { useForm, zodResolver } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { useQueryClient } from '@tanstack/react-query';
import {
  useNavigate,
  useParams,
  useRouter,
  useSearch
} from '@tanstack/react-router';
import { useEffect } from 'react';
import { z } from 'zod';

import {
  useArtistProfileAboutMutation,
  useArtistProfileAboutQuery
} from '@/shared/api/requests';

export const useArtistAbout = () => {
  const { id: profileId } = useParams({ from: '/artist/$id' });
  const queryClient = useQueryClient();
  const navigate = useNavigate({ from: '/artist/$id/onboard/$step' });
  const search = useSearch({ from: '/artist/$id/_onboard/onboard/$step' });
  const router = useRouter();

  const [opened, { close, open }] = useDisclosure(false);
  const about = useArtistProfileAboutQuery({ profileId });

  const next = () => {
    if (search.from !== undefined) {
      router.history.back();
    }
    open();
  };
  const aboutMutation = useArtistProfileAboutMutation({
    options: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ['ArtistProfileAbout', profileId]
        });
        next();
      }
    }
  });
  const form = useForm({
    initialValues: {
      about: ''
    },
    validate: zodResolver(
      z.object({
        about: z.string({ message: 'is required' }).min(50).max(1000)
      })
    )
  });

  useEffect(() => {
    if (about.isSuccess) {
      form.initialize(about.data.data);
    }
  }, [about.data?.data.about]);

  const onSubmit = form.onSubmit(async (value) => {
    await aboutMutation.mutateAsync({
      params: {
        profileId,
        data: value
      }
    });
  });

  const toProfile = () => {
    navigate({ to: '/artist/$id/profile' });
  };

  const queries = [about];

  const isLoading = queries.some((q) => q.isLoading);

  return {
    state: {
      isLoading,
      form,
      modal: {
        opened,
        close,
        open
      }
    },
    functions: {
      onSubmit,
      toProfile
    }
  };
};
